'use client'

import { ProductCard } from '@/feature/product/components/product-card/ProductCard.client'
import { useVisibleProductCards } from '@/feature/product/hooks/useVisibleProductCards'
import { pageListAnalyticsAtom } from '@/feature/product/services/product.store'
import { getProductClickContext } from '@/feature/product/utils/productListAnalytics'

import { useProductsListFavoriteStatus } from '@/feature/product/hooks/useProductsListFavoriteStatus'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import {
  type ProductCardFragment,
  ProductListPriceAndStockDocument,
  type ProductListProductsWithoutPriceFragment,
  ProductType,
} from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import { useSearchParams } from 'next/navigation'
import { Suspense, useEffect, useMemo, useRef } from 'react'
import { getProductListSource } from '../../services/productList.analytics'
import { type QueryKey } from '../../services/productList.types'

gql(/* GraphQL */ `
  query ProductListPriceAndStock($references: [String!]!) {
    productsByReferences(references: $references) {
      reference
      ...ProductCard_ProductPriceAndStock
    }
  }
`)

type ProductListProductsProps = {
  productList: ProductListProductsWithoutPriceFragment[]
  queryKey: QueryKey
}

export default function ProductListClient({ productList, queryKey }: ProductListProductsProps) {
  const references = productList.map((product) => product.reference)
  const referencesArticles = useMemo(
    () =>
      references.filter(
        (reference) =>
          productList.find((product) => product.reference === reference)?.type ===
          ProductType.Article,
      ),
    [productList, references],
  )

  const isAuthenticated = useIsAuthenticated()

  const { data: pricesAndStocks, loading } = useQuery(ProductListPriceAndStockDocument, {
    ...APOLLO_CLIENT_SIDE_OPTIONS,
    variables: { references },
  })

  // Keep cards in loading state for authenticated users until price/stock data arrives,
  // preventing a transient isNotSellable issue card (isSellable is missing from the SSR fragment)
  const isPriceAndStockLoading = loading || (isAuthenticated && !pricesAndStocks)

  const { data: favoritesMap } = useProductsListFavoriteStatus(referencesArticles)

  const pageListAnalytics = useAtomValue(pageListAnalyticsAtom)
  const searchParams = useSearchParams()

  const productCardsRefs = useRef<React.RefObject<HTMLDivElement | null>[]>([])

  const productClickContext = getProductClickContext(
    queryKey,
    !!searchParams.get('filter'),
    pageListAnalytics.title,
  )

  const { setProductCardsRefs, productCardsRefs: productCardsRefsHook } = useVisibleProductCards({
    itemListName: productClickContext,
  })

  useEffect(() => {
    productCardsRefs.current = productList.map(
      (_, index) => productCardsRefs.current[index] || { current: null },
    )
  }, [productList])

  useEffect(() => {
    if (
      productCardsRefs.current.length > 0 &&
      productCardsRefsHook.length !== productCardsRefs.current.length
    ) {
      setProductCardsRefs(productCardsRefs.current)
    }
  }, [productList, setProductCardsRefs, productCardsRefsHook.length])

  return (
    <Suspense
      fallback={productList.map((product, index) => {
        return (
          product.urlKey && (
            <ProductCard
              key={product.urlKey}
              product={product as ProductCardFragment}
              productPriceAndStock={{
                priceAndStockData: undefined,
                priceAndStockIsLoading: true,
              }}
              isFavorite={false}
              responsive
              position={index + 1}
            />
          )
        )
      })}
    >
      {productList.map((product, index) => (
        <ProductCard
          ref={productCardsRefs.current[index]}
          key={product.urlKey}
          product={product as ProductCardFragment}
          productPriceAndStock={{
            priceAndStockData: pricesAndStocks?.productsByReferences?.find(
              (priceAndStock) => priceAndStock.reference === product.reference,
            ),
            priceAndStockIsLoading: isPriceAndStockLoading,
          }}
          isFavorite={!!favoritesMap?.[product.reference]}
          responsive
          position={index + 1}
          source={getProductListSource({
            searchParams,
            pageListAnalytics,
            queryKey,
          })}
        />
      ))}
    </Suspense>
  )
}
